import React, { useState } from 'react';
import { Button, Card, Spinner, Table } from 'react-bootstrap';
import { useNamespacedTranslation } from '../../../../../hooks/useNamespacedTranslation';
import { EmployeeAchievementCard_AchievementFragment } from '../../../__generated__/graphql';
import { useUpdateObjectiveResults } from './useUpdateObjectiveResults';
import { useToasts } from '../../../../../context/Toast';
import type { GoalResultChange } from '../../../components/EmployeeFeedbackQuestionnaire/InlineGoalQuestion';
import { GoalAchievementRow } from './GoalAchievementRow';
import { EmployeeWithAvatar } from '../../../components/Employee/EmployeeWithAvatar';
import { InfoHint } from '../../../../../components/hint/InfoHint';
import { formatDecimal } from '../../../../../utils/number';

type AchievementData = EmployeeAchievementCard_AchievementFragment;

type Props = {
  achievement: AchievementData;
  canEditResults: boolean;
  onResultUpdated: () => void;
  onGoalClick: (objectiveId: string) => void;
};

const i18Path = 'performanceReview.goalResultsSection';

export const EmployeeAchievementCard: React.FC<
  React.PropsWithChildren<Props>
> = ({ achievement, canEditResults, onResultUpdated, onGoalClick }) => {
  const t = useNamespacedTranslation(i18Path);
  const { addError } = useToasts();

  const [goalResultChanges, setGoalResultChanges] = useState<
    GoalResultChange[]
  >([]);
  const [resetKey, setResetKey] = useState(0);

  const [updateObjectiveResults, { loading: isSaving }] =
    useUpdateObjectiveResults();

  const handleGoalResultChange = (change: GoalResultChange) => {
    if (!canEditResults) {
      return;
    }
    setGoalResultChanges((existing) => {
      const index = existing.findIndex(
        (e) => e.objectiveId === change.objectiveId,
      );
      if (index >= 0) {
        return existing.map((e, i) => (i === index ? change : e));
      }
      return [...existing, change];
    });
  };

  const handleSave = async () => {
    if (!canEditResults) {
      return;
    }
    try {
      const result = await updateObjectiveResults({
        variables: {
          input: {
            results: goalResultChanges.map((g) => ({
              objectiveId: g.objectiveId,
              predefinedResultOptionId: g.predefinedResultOptionId,
              result: g.result,
            })),
          },
        },
      });

      const errors =
        result.data?.objectives.updateObjectiveResults.errors ?? [];
      if (errors.length > 0) {
        errors.forEach((e) => addError(e));
      } else {
        setGoalResultChanges([]);
        onResultUpdated();
      }
    } catch {
      addError(t('saveFailed'));
    }
  };

  const handleCancel = () => {
    setGoalResultChanges([]);
    setResetKey((k) => k + 1);
  };

  return (
    <Card className="mb-3">
      <Card.Header
        className={achievement.totalGoals > 0 ? 'border-bottom-0' : ''}>
        <div className="d-flex justify-content-between w-100">
          <EmployeeWithAvatar
            employee={{
              name: achievement.employee.Name,
              avatarUrl: achievement.employee.avatar?.url,
            }}
            additionalInfo={achievement.employee.flair__Position__c}
            size="xs"
          />
          <span className="text-muted d-flex align-items-center gap-2">
            <span
              style={{
                display: 'inline-flex',
                width: 48,
                height: 8,
                borderRadius: 4,
                backgroundColor: 'var(--bs-secondary-bg)',
                overflow: 'hidden',
              }}>
              <span
                style={{
                  width: `${
                    achievement.totalGoals > 0
                      ? (achievement.goalsWithResult / achievement.totalGoals) *
                        100
                      : 0
                  }%`,
                  backgroundColor: 'var(--bs-success)',
                  borderRadius: 4,
                }}
              />
            </span>
            {t('goalsWithResult', {
              count: achievement.goalsWithResult,
              total: achievement.totalGoals,
            })}
          </span>
        </div>
      </Card.Header>
      <Card.Body className="p-0">
        {achievement.totalGoals === 0 ? (
          <p className="text-muted mb-0 text-center py-3">{t('noGoals')}</p>
        ) : (
          <Table borderless className="mb-0">
            <thead>
              <tr className="small text-muted fw-semibold">
                <th className="fw-semibold">{t('goalLabel')}</th>
                <th className="fw-semibold">{t('categoryLabel')}</th>
                <th className="fw-semibold">
                  {t('finalWeightsLabel')}{' '}
                  <InfoHint text={t('finalWeightsTooltip')} />
                </th>
                <th className="fw-semibold">
                  {t('scaleLabel')} <InfoHint text={t('scaleHeaderTooltip')} />
                </th>
                <th className="fw-semibold">{t('resultLabel')}</th>
                <th className="fw-semibold">
                  {t('achievementScoreLabel')}{' '}
                  <InfoHint text={t('achievementScoreTooltip')} />
                </th>
                <th className="fw-semibold">
                  {t('weightedScoreLabel')}{' '}
                  <InfoHint text={t('weightedScoreTooltip')} />
                </th>
              </tr>
            </thead>
            <tbody>
              {achievement.categories.flatMap((cat) =>
                cat.goals.map((goal) => (
                  <GoalAchievementRow
                    key={`${goal.objectiveId}-${resetKey}`}
                    goal={goal}
                    categoryName={cat.category.Name}
                    categoryWeight={cat.categoryWeight}
                    predefinedResultOptions={
                      cat.category.predefinedResultOptions
                    }
                    canEdit={canEditResults}
                    onChange={handleGoalResultChange}
                    onGoalClick={onGoalClick}
                  />
                )),
              )}
            </tbody>
            <tfoot>
              <tr>
                <td colSpan={7} className="text-end fw-bold py-3">
                  {t('finalScoreLabel')}:{' '}
                  {formatDecimal(achievement.finalWeightedScore)}
                </td>
              </tr>
            </tfoot>
          </Table>
        )}
        {canEditResults && goalResultChanges.length > 0 && (
          <div className="d-flex justify-content-end gap-2 mb-3 mx-2">
            <Button
              variant="outline-secondary"
              size="sm"
              disabled={isSaving}
              onClick={handleCancel}>
              {t('cancelButton')}
            </Button>
            <Button
              variant="primary"
              size="sm"
              disabled={isSaving}
              onClick={handleSave}>
              {isSaving && (
                <Spinner
                  as="span"
                  animation="border"
                  size="sm"
                  className="me-1"
                />
              )}
              {t('saveButton')}
            </Button>
          </div>
        )}
      </Card.Body>
    </Card>
  );
};
